"use client";

import React, { useEffect, useRef, useState } from "react";
import { Zap, Users, CircleDollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Voice Over IP",
  description: "Modernize your communication with high-fidelity VOIP systems. Our installations feature node-based architecture for maximum reliability and crystal-clear audio quality across your entire office network.",
  icon: Zap
}, {
  title: "Unified Comms",
  description: "Integrate your voice, video, and messaging into a single streamlined platform. Our expert technicians ensure seamless connectivity between your hardware and cloud-based communication software.",
  icon: Users
}, {
  title: "Scalable Infrastructure",
  description: "Build for today and grow for tomorrow. We design phone systems that scale effortlessly with your business, providing transparent cost structures and modular hardware upgrades as you expand.",
  icon: CircleDollarSign
}];
export default function CommercialWhyChooseUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="241715" id="business-phones" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center">
          <h2 className="font-default text-3xl font-bold tracking-tighter text-foreground md:text-5xl">
            Next-Gen Business Connectivity
          </h2>
          <p className="mt-4 text-lg font-mono text-muted-foreground">
            We are a locally operated company that takes our work seriously. We communicate clearly, arrive prepared, and treat every job with the same level of care regardless of size. Our goal is to be a technology partner our clients can rely on long term.
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div key={i} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Card className="h-full border-x-0 border-b-0 border-t-4 border-t-secondary bg-primary shadow-2xl border-white/10">
                  <CardHeader className="pb-4 relative overflow-hidden">
                    <span className="absolute -top-4 -right-2 font-default text-7xl font-bold text-secondary opacity-30 pointer-events-none">
                      0{i + 1}
                    </span>
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center bg-secondary/10">
                      <Icon className="h-6 w-6 text-secondary" />
                    </div>
                    <CardTitle className="font-default text-xl font-bold text-primary-foreground uppercase tracking-wider">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-mono text-sm text-primary-foreground/80 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}